const PENDING_GENERATION_KEY = 'snapfy_pending_generation';

export interface PendingGenerationData {
  prompt: string;
  styleDetails: { stylePrompt?: string };
  aspectRatio: string;
  imageBase64?: string;
  imageName?: string;
  imageType?: string;
}

export const savePendingGeneration = async (
  file: File | null,
  prompt: string,
  styleDetails: { stylePrompt?: string },
  aspectRatio: string
): Promise<void> => {
  const data: PendingGenerationData = {
    prompt,
    styleDetails,
    aspectRatio,
  };

  if (file) {
    // Convert file to base64
    const base64 = await fileToBase64(file);
    data.imageBase64 = base64;
    data.imageName = file.name;
    data.imageType = file.type;
  }

  // Guard against sessionStorage overflow (4MB limit)
  const MAX_PENDING_SIZE_BYTES = 4 * 1024 * 1024;
  const serialized = JSON.stringify(data);
  if (new Blob([serialized]).size > MAX_PENDING_SIZE_BYTES) {
    // Save without image — user will need to re-select
    delete data.imageBase64;
    delete data.imageName;
    delete data.imageType;
  }

  try {
    sessionStorage.setItem(PENDING_GENERATION_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Could not save pending generation: storage full');
  }
};

export const getPendingGeneration = (): PendingGenerationData | null => {
  const stored = sessionStorage.getItem(PENDING_GENERATION_KEY);
  if (!stored) return null;
  
  try {
    return JSON.parse(stored) as PendingGenerationData;
  } catch {
    return null;
  }
};

export const clearPendingGeneration = (): void => {
  sessionStorage.removeItem(PENDING_GENERATION_KEY);
};

export const restorePendingFile = async (data: PendingGenerationData): Promise<File | null> => {
  if (!data.imageBase64 || !data.imageName || !data.imageType) {
    return null;
  }

  try {
    const blob = base64ToBlob(data.imageBase64, data.imageType);
    return new File([blob], data.imageName, { type: data.imageType });
  } catch {
    return null;
  }
};

// Helper functions
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

const base64ToBlob = (base64: string, mimeType: string): Blob => {
  // Remove data URL prefix if present
  const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};
